import { BaseComponent } from "utils/base-component"
import throttle from "utils/throttle"

import { LeafletConfig } from "./leaflet-config"
import { LeafletRepository } from "./repository"
import { FilterOverlay } from "./filter-overlay"
import "./leaflet.scss"
import "./static/placeholder-icon.svg"
import "./static/placeholder.jpg"

export interface MapConfig {
  filename: string
  lat: number
  lng: number

  topoUrl: string
  pistesUrl: string

  showLiveSnowOnMap: boolean

  screenshotMode: boolean
  // dfp: DFP | undefined
  showShops: boolean
}

type Config = {
  level: string
  isMemberMode: boolean
  mapConfigList: MapConfig[]
}

export class Map extends BaseComponent<Config> {
  listener?: () => void

  static cssClass = "leaflet"

  init() {
    const { loadingStrategy } = this.root.dataset

    if (loadingStrategy === "pointer-over-container") {
      this.listener = this.pointerOver.bind(this)
      this.root.addEventListener("pointerover", this.listener)
    } else if (loadingStrategy === "pointer-over-document") {
      this.listener = this.pointerOverDocument.bind(this)
      document.body.addEventListener("pointerover", this.listener)
    } else if (loadingStrategy === "in-viewport") {
      this.listener = throttle(() => {
        this.loadMapIfInViewport()
      }, 500)

      window.addEventListener("scroll", this.listener)
      this.loadMapIfInViewport()
    } else if (loadingStrategy === "instant") {
      this.chunk()
    }
  }

  private loadMapIfInViewport() {
    if (!this.inViewport()) return

    if (this.listener) {
      window.removeEventListener("scroll", this.listener)
      this.listener = undefined
    }

    this.chunk()
  }

  private inViewport() {
    const bounding = this.root.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight

    return (
      (bounding.top >= 0 && bounding.top <= viewportHeight) ||
      (bounding.bottom >= 0 && bounding.top <= viewportHeight)
    )
  }

  private pointerOverDocument() {
    if (this.listener) {
      document.body.removeEventListener("pointerover", this.listener)
      this.listener = undefined
    }

    this.chunk()
  }

  private pointerOver() {
    if (this.listener) {
      this.root.removeEventListener("pointerover", this.listener)
      this.listener = undefined
    }

    this.chunk()
  }

  private chunk() {
    const { mapId } = this.root.dataset
    if (mapId === undefined) return

    this.toggleModifier("initial-loading", true)

    import(/* webpackChunkName: 'leaflet' */ "./leaflet").then((module) => {
      const repository = new LeafletRepository(
        this.config.level,
        this.config.isMemberMode,
        () => {
          this.toggleModifier("loading", true)
        },
        () => {
          this.toggleModifier("loading", false)
        },
      )
      const mapConfig: MapConfig = this.config.mapConfigList[Number(mapId)]
      const leafletConfig: LeafletConfig = {
        repository,

        locationName: mapConfig.filename,
        LatLng: [mapConfig.lat, mapConfig.lng],

        urlTemplates: {
          topo: mapConfig.topoUrl,
          piste: mapConfig.pistesUrl,
        },

        shouldShowLiveSnow: mapConfig.showLiveSnowOnMap,
        liveSnowElement: this.root.querySelector(".leaflet-live-snow") || null,

        screenshotMode: mapConfig.screenshotMode,
        // programmaticOptions: mapConfig.dfp ? selectOption(mapConfig.dfp) : null
        showShops: mapConfig.showShops,
      }

      // eslint-disable-next-line no-new
      const map = new module.Leaflet(this.root, leafletConfig)
      FilterOverlay.init(this.root, { map })

      this.toggleModifier("initial-loading", false)
      this.element("placeholder")?.element.remove()
    })
  }
}
